/**
 * cf-account.js - Click Fencing customer accounts, stage one: saved plans that
 * follow the customer instead of the browser.
 *
 * THE PROBLEM THIS FIXES. Saved plans lived only in localStorage, per calculator
 * (flattop_quotes, blade_quotes, perfmesh_quotes, ullc_quotes). Clear cookies,
 * switch to a phone, or come back on a work laptop and they were gone.
 *
 * HOW IDENTITY WORKS, AND WHY IT IS SAFE. Everything goes through the Shopify App
 * Proxy at /apps/cf-account. Shopify adds logged_in_customer_id and an HMAC of the
 * query signed with the app secret; the worker verifies that before it will read or
 * write a single row. This page NEVER tells the server who the customer is - a page
 * can claim any id it likes. Verified 2026-09-01: forged signature, forged write and
 * missing signature are all rejected 401.
 *
 * PLAN IDENTITY. A plan's local quoteNum comes from a per-browser counter, so two
 * devices would both mint "1" for different fences and a merge on that would
 * silently overwrite one with the other. The stable key is calcType + generatedAt,
 * an ISO timestamp with milliseconds. quoteNum is renumbered on the way down.
 *
 * SAVING NO LONGER REQUIRES SIGN-IN (Pete, 2026-09-01, later the same day - this
 * REVERSES his earlier call recorded above the original version of this file).
 *
 * WHY IT CHANGED. The gate was implemented by wrapping localStorage.setItem and, when
 * signed out, HOLDING the write and showing a sign-in prompt. Reproduced live: one Add
 * to Cart on flat top while signed out left flattop_quote_counter at 1 and
 * flattop_quotes EMPTY - the quote was discarded. Worse, the prompt renders on the
 * CALCULATOR page while Add to Cart immediately redirects to /pages/quote-review, so the
 * customer lands on an empty Quotes page with their quote gone and no explanation in
 * sight. That is the dead end Pete had just told us to stop creating.
 *
 * WHAT IT DOES NOW. The plan is ALWAYS saved locally, for everyone, signed in or not.
 * Signing in is what makes a plan follow you to another device - that is the thing an
 * account genuinely buys - and the sync below still does exactly that. No modal
 * interrupts the save, matching Pete's standing line about not pushing things at people
 * who are mid-purchase.
 */
(function () {
  'use strict';

  if (window.__cfAccount) return;
  window.__cfAccount = true;

  var PROXY = '/apps/cf-account';
  var SHOP_ACCOUNT = 'https://shopify.com/57065308254/account';
  var PLANS_PAGE = '/pages/quote-review';
  var QUOTE_KEYS = ['flattop_quotes', 'blade_quotes', 'perfmesh_quotes', 'ullc_quotes'];

  var state = { signedIn: null, checked: false, pending: null };

  function log() {
    if (window.console && console.debug) console.debug.apply(console, ['[cf-account]'].concat([].slice.call(arguments)));
  }

  function safeParse(raw, fallback) {
    try { var v = JSON.parse(raw); return v === null || v === undefined ? fallback : v; }
    catch (e) { return fallback; }
  }

  function planKey(q) {
    if (!q || typeof q !== 'object') return null;
    var calc = q.calcType || 'unknown';
    var stamp = q.generatedAt || q.quoteNum;
    return stamp ? calc + ':' + stamp : null;
  }

  function storageKeyFor(calcType) {
    var map = { FlatTop: 'flattop_quotes', Blade: 'blade_quotes', PerfMesh: 'perfmesh_quotes', Frameless: 'ullc_quotes' };
    return map[calcType] || null;
  }

  // Captured BEFORE the gate is installed so our own writes are never bounced by
  // our own wrapper.
  var nativeSetItem = window.localStorage.setItem.bind(window.localStorage);

  function readLocalPlans() {
    var out = [];
    QUOTE_KEYS.forEach(function (key) {
      var arr = safeParse(window.localStorage.getItem(key), []);
      if (!Array.isArray(arr)) return;
      arr.forEach(function (q) {
        var k = planKey(q);
        if (k) out.push({ key: k, storageKey: key, quote: q });
      });
    });
    return out;
  }

  function writeLocalPlans(storageKey, quotes) {
    try {
      nativeSetItem(storageKey, JSON.stringify(quotes));
      var prefix = storageKey.replace('_quotes', '');
      var highest = quotes.reduce(function (m, q) {
        var n = parseInt(q.quoteNum, 10);
        return Number.isFinite(n) && n > m ? n : m;
      }, 0);
      nativeSetItem(prefix + '_quote_counter', String(highest));
    } catch (e) { log('local write failed', e); }
  }

  function apiGet(path) {
    return fetch(PROXY + path, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  }
  function apiPost(path, body) {
    return fetch(PROXY + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  // FAIL OPEN unless Shopify explicitly says "not signed in".
  //
  // The save gate blocks a write when signedIn === false. If a network blip, a proxy
  // misconfiguration or a 5xx also set false, then NOBODY could save a plan - which
  // is worse than the behaviour we are replacing, where anyone could. So only a real
  // 401 counts as signed out. Anything else leaves the state UNKNOWN (null), and the
  // gate lets the write through.
  function checkSession() {
    return apiGet('/quotes').then(function (r) {
      if (r.status === 401) { state.signedIn = false; state.checked = true; return { signedIn: false, quotes: [] }; }
      if (!r.ok) { state.signedIn = null; state.checked = true; return { signedIn: false, unknown: true, quotes: [], error: r.status }; }
      return r.json().then(function (d) {
        state.signedIn = true; state.checked = true;
        return { signedIn: true, quotes: (d && d.quotes) || [] };
      })['catch'](function (e) {
        state.signedIn = null; state.checked = true;
        return { signedIn: false, unknown: true, quotes: [], error: String(e) };
      });
    })['catch'](function (e) {
      state.signedIn = null; state.checked = true;
      return { signedIn: false, unknown: true, quotes: [], error: String(e) };
    });
  }

  function toServerShape(entry) {
    var q = entry.quote;
    return {
      quoteKey: entry.key,
      calcType: q.calcType,
      quoteName: q.quoteName || null,
      pricingType: q.pricingType || null,
      grandTotal: parseFloat(q.grandTotal) || null,
      currency: q.currency || 'AUD',
      device: (window.innerWidth || 0) < 768 ? 'mobile' : 'desktop',
      quote: q
    };
  }

  function sync() {
    return checkSession().then(function (session) {
      if (!session.signedIn) return session;

      var local = readLocalPlans();
      var localKeys = {};
      local.forEach(function (e) { localKeys[e.key] = true; });

      var serverKeys = {};
      (session.quotes || []).forEach(function (s) { if (s && s.quoteKey) serverKeys[s.quoteKey] = true; });

      var toUpload = local.filter(function (e) { return !serverKeys[e.key]; }).map(toServerShape);
      var toDownload = (session.quotes || []).filter(function (s) { return s && s.quoteKey && !localKeys[s.quoteKey] && s.quote; });

      var grouped = {};
      toDownload.forEach(function (s) {
        var storageKey = storageKeyFor(s.quote.calcType) || storageKeyFor(s.calcType);
        if (!storageKey) return;
        (grouped[storageKey] = grouped[storageKey] || []).push(s.quote);
      });
      Object.keys(grouped).forEach(function (storageKey) {
        var existing = safeParse(window.localStorage.getItem(storageKey), []);
        if (!Array.isArray(existing)) existing = [];
        var next = existing.reduce(function (m, q) {
          var n = parseInt(q.quoteNum, 10); return Number.isFinite(n) && n > m ? n : m;
        }, 0);
        grouped[storageKey].forEach(function (q) { next += 1; q.quoteNum = String(next); existing.push(q); });
        writeLocalPlans(storageKey, existing);
      });

      var uploaded = Promise.resolve();
      if (toUpload.length) uploaded = apiPost('/quotes', { quotes: toUpload });

      return uploaded.then(function () {
        log('synced: ' + toUpload.length + ' up, ' + toDownload.length + ' down');
        document.dispatchEvent(new CustomEvent('cf:account-synced', {
          detail: { up: toUpload.length, down: toDownload.length }
        }));
        return { signedIn: true, up: toUpload.length, down: toDownload.length };
      });
    });
  }

  function signInUrl() {
    try { return SHOP_ACCOUNT + '?return_url=' + encodeURIComponent(location.pathname + location.search); }
    catch (e) { return SHOP_ACCOUNT; }
  }

  function showSignInPrompt(planName) {
    if (document.querySelector('.cf-acct-modal')) return;
    var safeName = planName ? String(planName).replace(/[<>&]/g, '') : 'your fence plan';
    var wrap = document.createElement('div');
    wrap.className = 'cf-acct-modal';
    wrap.innerHTML =
      '<div class="cf-acct-modal__box" role="dialog" aria-modal="true" aria-label="Sign in to save your plan">' +
      '<h2>Sign in to save this plan</h2>' +
      '<p>Saving keeps <strong>' + safeName + '</strong> on your account, so it is there on your phone, ' +
      'your laptop, and next time you come back.</p>' +
      '<p class="cf-acct-modal__note">Your quote stays on screen either way &mdash; you can keep building without an account.</p>' +
      '<div class="cf-acct-modal__actions">' +
      '<a class="cf-acct-btn cf-acct-btn--primary" href="' + signInUrl() + '">Sign in or create an account</a>' +
      '<button type="button" class="cf-acct-btn cf-acct-btn--ghost" data-cf-acct-dismiss>Not now</button>' +
      '</div></div>';
    wrap.addEventListener('click', function (e) {
      if (e.target === wrap || (e.target.hasAttribute && e.target.hasAttribute('data-cf-acct-dismiss'))) wrap.remove();
    });
    document.body.appendChild(wrap);
  }

  /* The save gate is GONE (2026-09-01). It used to wrap localStorage.setItem and hold
     the write when signed out, which silently discarded the customer's quote. Saving is
     now unconditional; sign-in only governs whether the plan syncs to the account. If a
     hard gate is ever wanted again, put it on the SYNC, never on the local write. */

  function boot() {
    sync().then(function (res) {
      document.dispatchEvent(new CustomEvent('cf:account-ready', { detail: { signedIn: !!(res && res.signedIn) } }));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.cfAccount = {
    sync: sync,
    state: state,
    isSignedIn: function () { return state.signedIn; },
    signInUrl: signInUrl,
    urls: { account: SHOP_ACCOUNT, purchases: SHOP_ACCOUNT + '/orders', plans: PLANS_PAGE }
  };
})();
